import React, { useEffect, useState } from "react";
import { Button, Input } from "@mui/joy";
import { Add } from "@mui/icons-material";
import { toast } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import { getFetch, postFetch } from "../../../../../../fetch/fetch";
import styles from "./staffmanage.module.css";
import PartTable from "./parttable";
import PartEditModal from "./parteditmodal";
import { PartVars, MappingStatus } from "./contractvarkeys";

//계약서 변수 관리 — ① 공통값 / ② 지점 정보 / ③ 파트 목록. 패턴: biostarenrolldevicesetting.tsx (섹션별 dirty + 저장 한 번)
const API = "/dashboard/employeedirectory";
const toastOptions: any = { position: "bottom-right", autoClose: 3000, hideProgressBar: true, theme: "colored" };

interface Settings {
    businessName: string;
    hourlyWage: string;
    fullAttendanceAllowance: string;
    baseHourlyWage: string;
}

const SETTING_FIELDS: { key: keyof Settings; label: string }[] = [
    { key: "businessName", label: "사업체명" },
    { key: "hourlyWage", label: "시급" },
    { key: "fullAttendanceAllowance", label: "만근수당" },
    { key: "baseHourlyWage", label: "기본시급" },
];

const emptySettings = (): Settings => ({ businessName: "", hourlyWage: "", fullAttendanceAllowance: "", baseHourlyWage: "" });

interface Branch {
    id: number;
    branchKey: string;
    displayName: string;
    representative: string;
    address: string;
    corporation: string;
    locationKeys: string[];
    sortOrder: number;
}

export interface Part {
    id: number;
    name: string;
    templateId: string | null;
    templateName: string;
    vars: PartVars;
    fieldMapping: { [fieldId: string]: string | null };
    templateFields: any[];
    sortOrder: number;
    mappingStatus: MappingStatus;
}

const errorText = (result: any, fallback: string) => {
    if (result && result.message === "NOT_AUTHORIZED") return "권한이 없습니다 (todoistApiKey 필요)";
    if (result && result.detail) return `${fallback} (${result.detail})`;
    return result && result.message ? `${fallback} (${result.message})` : fallback;
};

const ContractSettings: React.FC<any> = (props) => {

    const [settings, setSettings] = useState<Settings>(emptySettings());
    const [settingsDirty, setSettingsDirty] = useState(false);
    const [savingSettings, setSavingSettings] = useState(false);

    const [branches, setBranches] = useState<Branch[]>([]);
    const [branchesDirty, setBranchesDirty] = useState(false);
    const [savingBranches, setSavingBranches] = useState(false);

    const [parts, setParts] = useState<Part[]>([]);
    const [loading, setLoading] = useState(false);
    const [partsLoading, setPartsLoading] = useState(false);
    const [editOpen, setEditOpen] = useState(false);
    const [editTarget, setEditTarget] = useState<Part | null>(null);

    useEffect(() => {
        loadAll();
    }, []);

    //최초 3개 동시 로드
    const loadAll = async () => {
        try {
            setLoading(true);
            const [s, b, p]: any[] = await Promise.all([
                getFetch(`${API}/settings`),
                getFetch(`${API}/branches`),
                getFetch(`${API}/parts`)
            ]);
            if (s.message === "success") {
                const next = emptySettings();
                SETTING_FIELDS.forEach((f) => { next[f.key] = String(s.settings && s.settings[f.key] !== undefined && s.settings[f.key] !== null ? s.settings[f.key] : ""); });
                setSettings(next);
                setSettingsDirty(false);
            } else {
                toast.error(errorText(s, "공통값을 불러오지 못했습니다"), toastOptions);
            }
            if (b.message === "success") {
                setBranches((b.branches || []).map((row: any) => ({
                    ...row,
                    representative: String(row.representative || ""),
                    address: String(row.address || ""),
                    corporation: String(row.corporation || ""),
                    locationKeys: Array.isArray(row.locationKeys) ? row.locationKeys : []
                })));
                setBranchesDirty(false);
            } else {
                toast.error(errorText(b, "지점 정보를 불러오지 못했습니다"), toastOptions);
            }
            if (p.message === "success") {
                setParts(p.parts || []);
            } else {
                toast.error(errorText(p, "파트 목록을 불러오지 못했습니다"), toastOptions);
            }
        } catch (e) {
            console.log(e);
            toast.error("오류가 발생했습니다", toastOptions);
        } finally {
            setLoading(false);
        }
    };

    //파트 저장/삭제 후에는 파트만 재조회 (①②의 편집 중인 값을 덮지 않게)
    const loadParts = async () => {
        try {
            setPartsLoading(true);
            const p: any = await getFetch(`${API}/parts`);
            if (p.message === "success") {
                setParts(p.parts || []);
            } else {
                toast.error(errorText(p, "파트 목록을 불러오지 못했습니다"), toastOptions);
            }
        } catch (e) {
            console.log(e);
        } finally {
            setPartsLoading(false);
        }
    };

    //① 공통값 저장
    const saveSettings = async () => {
        try {
            setSavingSettings(true);
            const result: any = await postFetch(`${API}/settings`, { settings });
            if (result.message === "success") {
                toast.success("저장되었습니다", toastOptions);
                setSettingsDirty(false);
            } else {
                toast.error(errorText(result, "저장에 실패했습니다"), toastOptions);
            }
        } catch (e) {
            console.log(e);
            toast.error("오류가 발생했습니다", toastOptions);
        } finally {
            setSavingSettings(false);
        }
    };

    const updateBranch = (id: number, patch: Partial<Branch>) => {
        setBranches((prev) => prev.map((b) => (b.id === id ? { ...b, ...patch } : b)));
        setBranchesDirty(true);
    };

    //② 지점 정보 저장 (12행 전부)
    const saveBranches = async () => {
        try {
            setSavingBranches(true);
            const result: any = await postFetch(`${API}/branches`, {
                branches: branches.map((b) => ({ id: b.id, representative: b.representative, address: b.address, corporation: b.corporation }))
            });
            if (result.message === "success") {
                toast.success("저장되었습니다", toastOptions);
                setBranchesDirty(false);
            } else {
                toast.error(errorText(result, "저장에 실패했습니다"), toastOptions);
            }
        } catch (e) {
            console.log(e);
            toast.error("오류가 발생했습니다", toastOptions);
        } finally {
            setSavingBranches(false);
        }
    };

    const nextSortOrder = parts.length ? Math.max(...parts.map((p) => +p.sortOrder || 0)) + 1 : 1;

    return (
        <div>

            {/* ① 공통값 */}
            <div className={styles.sectionTitle}>① 공통값</div>
            <div className={styles.section}>
                <div style={{ display: "flex", gap: "16px", padding: "8px 0 16px" }}>
                    {SETTING_FIELDS.map((f) => (
                        <div key={f.key} style={{ flex: 1 }}>
                            <div className={styles.fieldLabel}>{f.label}</div>
                            <Input
                                size="sm"
                                value={settings[f.key]}
                                disabled={loading}
                                onChange={(e) => { const v = e.target.value; setSettings((prev) => ({ ...prev, [f.key]: v })); setSettingsDirty(true); }}
                            />
                        </div>
                    ))}
                </div>
            </div>
            <div className={styles.sectionFooter}>
                <div className={styles.hint}>사업체명·시급·만근수당·기본시급은 모든 계약서에 공통으로 들어갑니다. 직원별 예외는 직원명부의 변수 조정에서.</div>
                <Button variant="solid" onClick={saveSettings} loading={savingSettings} disabled={!settingsDirty || loading}>저장</Button>
            </div>

            {/* ② 지점 정보 */}
            <div className={styles.sectionTitle}>② 지점 정보</div>
            <div className={styles.section}>
                <div style={{ display: "flex", alignItems: "center", padding: "10px 4px", borderBottom: "1px solid #e0e0e0", fontSize: "13px", color: "#888", gap: "12px" }}>
                    <div style={{ width: "130px", flexShrink: 0 }}>지점</div>
                    <div style={{ width: "160px", flexShrink: 0 }}>대표자</div>
                    <div style={{ flex: 1 }}>소재지</div>
                    <div style={{ width: "220px", flexShrink: 0 }}>법인</div>
                </div>
                {branches.map((b) => (
                    <div key={b.id} style={{ display: "flex", alignItems: "center", padding: "8px 4px", borderBottom: "1px solid #f0f0f0", gap: "12px" }}>
                        <div style={{ width: "130px", flexShrink: 0 }}>
                            <div style={{ fontFamily: "Apple_SB", fontSize: "14px" }}>{b.displayName}</div>
                            <div style={{ fontSize: "11px", color: "#9ca3af", marginTop: "2px" }}>{b.locationKeys.join(", ")}</div>
                        </div>
                        <Input size="sm" sx={{ width: "160px", flexShrink: 0 }} value={b.representative} onChange={(e) => updateBranch(b.id, { representative: e.target.value })} />
                        <Input size="sm" sx={{ flex: 1, minWidth: 0 }} value={b.address} onChange={(e) => updateBranch(b.id, { address: e.target.value })} />
                        <Input size="sm" sx={{ width: "220px", flexShrink: 0 }} value={b.corporation} onChange={(e) => updateBranch(b.id, { corporation: e.target.value })} />
                    </div>
                ))}
                {!loading && branches.length === 0 &&
                    <div style={{ padding: "30px 4px", fontFamily: "Apple_R", color: "#9ca3af" }}>지점 정보가 없습니다 (마이그레이션 시드 확인)</div>
                }
            </div>
            <div className={styles.sectionFooter}>
                <div className={styles.hint}>대치 2·3·6층은 계약서 값이 같아 한 줄로 관리합니다 (회색 글씨 = 이 값을 쓰는 CRM 지점 코드).</div>
                <Button variant="solid" onClick={saveBranches} loading={savingBranches} disabled={!branchesDirty || loading}>저장</Button>
            </div>

            {/* ③ 파트 목록 */}
            <div className={styles.sectionTitle}>
                <span>③ 파트 목록</span>
                <Button size="sm" variant="solid" startDecorator={<Add />} onClick={() => { setEditTarget(null); setEditOpen(true); }}>파트 추가</Button>
            </div>
            <div className={styles.section}>
                <PartTable parts={parts} loading={loading || partsLoading} onEdit={(p: Part) => { setEditTarget(p); setEditOpen(true); }} />
            </div>
            <div className={styles.sectionFooter}>
                <div className={styles.hint}>매핑 = 탬플릿의 빈칸마다 어떤 값을 넣을지 연결한 상태. "필드 N개 미연결"이면 편집에서 연결해야 그 파트 계약서를 보낼 수 있습니다.</div>
            </div>

            <PartEditModal
                open={editOpen}
                part={editTarget}
                nextSortOrder={nextSortOrder}
                onClose={() => setEditOpen(false)}
                onSaved={loadParts}
            />

        </div>
    );
};

export default ContractSettings;
